import React, { useCallback, useEffect, useRef, useState } from "react";
import { VIEWER_PASSPHRASE } from "./lib/config.js";
import { DataProvider, useData } from "./lib/store.jsx";
import Overview from "./tabs/Overview.jsx";
import Financial from "./tabs/Financial.jsx";
import Occupancy from "./tabs/Occupancy.jsx";
import Staffing from "./tabs/Staffing.jsx";
import Clinical from "./tabs/Clinical.jsx";
import Website from "./tabs/Website.jsx";
import Forecast from "./tabs/Forecast.jsx";

/* A dashboard, not a page: one board per tab, switched in place like the
   rooms on a smart-home panel. The hash keeps a tab linkable. */
const SECTIONS = [
  { id: "overview", nav: "Overview", Body: Overview },
  { id: "financial", nav: "Financial", Body: Financial },
  { id: "occupancy", nav: "Occupancy", Body: Occupancy },
  { id: "staffing", nav: "Staffing", Body: Staffing },
  { id: "clinical", nav: "Wellbeing", Body: Clinical },
  { id: "website", nav: "Website", Body: Website },
];

/* Fixed, blurred "room" behind the glass. */
const Aura = () => (
  <div className="aura" aria-hidden="true"><i /><i /><i /><i /></div>
);

const fromHash = () => {
  const id = window.location.hash.replace("#", "");
  return SECTIONS.some((s) => s.id === id) ? id : "overview";
};

/* Ali's budget page lives on its own subdomain, forecast.ariacarehome.app,
   served from this same build. It is never in the dashboard nav. */
const isForecast = () => window.location.hostname.startsWith("forecast.");

function Tabs({ active, go }) {
  const rail = useRef(null);
  useEffect(() => {
    const el = rail.current?.querySelector(".tab.on");
    if (el && rail.current.scrollWidth > rail.current.clientWidth) {
      el.scrollIntoView({ block: "nearest", inline: "center" });
    }
  }, [active]);
  return (
    <div className="tabs" role="tablist" ref={rail}>
      {SECTIONS.map((s) => (
        <button key={s.id} role="tab" id={`tab-${s.id}`}
          aria-selected={active === s.id} aria-controls="board"
          className={"tab" + (active === s.id ? " on" : "")}
          onClick={() => go(s.id)}>{s.nav}</button>
      ))}
    </div>
  );
}

function Shell() {
  const { status, loadedAt } = useData();
  const [active, setActive] = useState(fromHash);

  const go = useCallback((id) => {
    setActive(id);
    history.replaceState(null, "", `#${id}`);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);
  useEffect(() => {
    const on = () => setActive(fromHash());
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);

  const stamp = loadedAt
    ? `Updated ${loadedAt.toLocaleDateString("en-GB",
      { day: "numeric", month: "short" })}` : null;

  if (status === "loading") {
    return (
      <div className="app">
        <Aura />
        <div className="loading"><div className="spin" />Loading data…</div>
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="app">
        <Aura />
        <div className="loading" style={{ color: "var(--neg)" }}>
          Could not load data.
        </div>
      </div>
    );
  }

  const { Body } = SECTIONS.find((s) => s.id === active);
  return (
    <div className="app">
      <Aura />
      <div className="frame">
        <header className="topbar">
          <img className="brand" src="/aria-icon.png" alt="Aria Care Home" />
          <Tabs active={active} go={go} />
          {stamp && <div className="stamp">{stamp}</div>}
        </header>
        <main id="board" className="board" role="tabpanel"
          aria-labelledby={`tab-${active}`} key={active}>
          <div className="bento"><Body /></div>
        </main>
      </div>
    </div>
  );
}

function ForecastShell() {
  const { status, loadedAt } = useData();
  const stamp = loadedAt
    ? `Updated ${loadedAt.toLocaleDateString("en-GB",
      { day: "numeric", month: "short" })}` : null;
  return (
    <div className="app navy">
      <Aura />
      {status === "loading" || status === "error" ? (
        <div className="loading" style={status === "error" ? { color: "var(--neg)" } : undefined}>
          {status === "error" ? "Could not load data." : <><div className="spin" />Loading data…</>}
        </div>
      ) : (
        <div className="frame">
          <header className="topbar">
            <img className="brand" src="/aria-icon.png" alt="Aria Care Home" />
            {stamp && <div className="stamp">{stamp}</div>}
          </header>
          <main className="board">
            <div className="bento"><Forecast /></div>
          </main>
        </div>
      )}
    </div>
  );
}

export default function RootGate() {
  const [ok, setOk] = useState(
    () => sessionStorage.getItem("aria-auth") === "1");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState(false);

  if (ok) return (
    <DataProvider>{isForecast() ? <ForecastShell /> : <Shell />}</DataProvider>
  );

  function submit(e) {
    e.preventDefault();
    if (pw === VIEWER_PASSPHRASE) {
      sessionStorage.setItem("aria-auth", "1");
      setOk(true);
    } else setErr(true);
  }

  return (
    <div className="gate">
      <Aura />
      <form onSubmit={submit} className="card" style={{ width: 400 }}>
        <div style={{ display: "flex", gap: 13, alignItems: "center",
          marginBottom: 26 }}>
          <img src="/aria-icon.png" alt="Aria"
            style={{ height: 34, width: "auto", objectFit: "contain" }} />
          <div>
            <div className="label">Aria Care Home</div>
            <div style={{ fontSize: 21, fontWeight: 600, marginTop: 2,
              letterSpacing: "-.02em" }}>Secure access</div>
          </div>
        </div>
        <div className="label" style={{ marginBottom: 8 }}>Passphrase</div>
        <input autoFocus type="password" value={pw}
          onChange={(e) => { setPw(e.target.value); setErr(false); }}
          placeholder="Enter passphrase"
          style={err ? { borderColor: "var(--neg)" } : undefined} />
        {err && <div className="tiny" style={{ color: "var(--neg)",
          marginTop: 10 }}>Incorrect passphrase.</div>}
        <button type="submit" className="btn">Unlock dashboard</button>
        <div className="tiny muted" style={{ marginTop: 20, lineHeight: 1.5 }}>
          Restricted to authorised operators and invited investors.
        </div>
      </form>
    </div>
  );
}
